import { Popover, type PopoverOrigin } from '@mui/material';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import clsx from 'clsx';
import { Arrow } from 'components/Svg/Arrow';
import { ArrowRight } from 'components/Svg/ArrowRight';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { TextLink } from 'components/UI/Link/TextLink';
import { useRouter } from 'next/router';
import React, { type MouseEventHandler, useCallback, useState } from 'react';

import styles from './SortFilter.module.scss';

export const ColumnHeaders: ISortFilterItem[] = [
  {
    key: 1,
    name: 'portalRank',
    displayName: 'Rank',
    ariaLabel: 'transfer-portal-rank',
    flipDirection: false,
    isLogo: false,
  },
  {
    key: 2,
    name: 'jerseyNumber',
    displayName: '#',
    ariaLabel: 'jersey-number',
    flipDirection: false,
    isLogo: false,
  },
  {
    key: 3,
    name: 'rank',
    displayName: 'Rank',
    ariaLabel: 'overall-industry-rank',
    flipDirection: true,
    isLogo: false,
  },
  {
    key: 4,
    name: 'lastName',
    displayName: 'Player',
    ariaLabel: 'player-name',
    flipDirection: false,
    isLogo: false,
  },
  {
    key: 5,
    name: 'Industry',
    displayName: 'industry',
    ariaLabel: 'rivals-industry',
    flipDirection: false,
    isLogo: true,
  },
  {
    key: 6,
    name: 'On3',
    displayName: 'On3',
    ariaLabel: 'on3',
    flipDirection: false,
    isLogo: true,
  },
  {
    key: 7,
    name: '247',
    displayName: '247',
    ariaLabel: '247',
    flipDirection: false,
    isLogo: true,
  },
  {
    key: 8,
    name: 'Espn',
    displayName: 'ESPN',
    ariaLabel: 'espn',
    flipDirection: false,
    isLogo: true,
  },
  {
    key: 9,
    name: 'Rivals',
    displayName: 'Rivals',
    ariaLabel: 'rivals',
    flipDirection: false,
    isLogo: true,
  },
  {
    key: 10,
    name: 'status',
    displayName: 'Status',
    ariaLabel: 'status',
    flipDirection: false,
    isLogo: false,
  },
  {
    key: 11,
    name: 'date',
    displayName: 'Status',
    ariaLabel: 'status-date',
    flipDirection: false,
    isLogo: false,
  },
  {
    key: 12,
    name: 'position',
    displayName: 'Pos',
    ariaLabel: 'position',
    flipDirection: false,
    isLogo: false,
  },
  {
    key: 13,
    name: 'height',
    displayName: 'Ht',
    ariaLabel: 'height',
    flipDirection: true,
    isLogo: false,
  },
  {
    key: 14,
    name: 'weight',
    displayName: 'Wt',
    ariaLabel: 'weight',
    flipDirection: true,
    isLogo: false,
  },
  {
    key: 15,
    name: 'nilValue',
    displayName: 'NIL',
    ariaLabel: 'nil-value',
    flipDirection: true,
    isLogo: false,
  },
  {
    key: 16,
    name: 'rating',
    displayName: 'Rating',
    ariaLabel: 'rating',
    flipDirection: true,
    isLogo: false,
  },
  {
    key: 17,
    name: 'athleteVerified',
    displayName: 'Athlete-Verified',
    ariaLabel: 'athlete-verified',
    flipDirection: false,
    isLogo: false,
  },
  {
    key: 18,
    ariaLabel: 'class-rank',
    name: 'classRank',
    displayName: 'Class',
    flipDirection: false,
  },
  {
    key: 19,
    ariaLabel: 'age',
    name: 'age',
    displayName: 'Age',
    flipDirection: true,
  },
];

export type ColumnOption =
  | 'portalRank'
  | 'jerseyNumber'
  | 'rank'
  | 'lastName'
  | 'Industry'
  | 'On3'
  | '247'
  | 'Espn'
  | 'Rivals'
  | 'status'
  | 'date'
  | 'position'
  | 'height'
  | 'weight'
  | 'nilValue'
  | 'rating'
  | 'athleteVerified'
  | 'name'
  | 'age'
  | 'classRank';

export const getColumnHeaderList = (
  names: ColumnOption[],
): ISortFilterItem[] => {
  const headerMap = new Map(ColumnHeaders.map((item) => [item.name, item]));

  return names
    .map((name) => headerMap.get(name))
    .filter((item): item is ISortFilterItem => !!item);
};

export interface ISortFilterItem {
  /**used for react keys must be unique */
  key?: number;
  /**name API uses*/
  name: string;
  /**Name used in dropdown list */
  displayName?: string;
  /**Flips default sort direction */
  flipDirection?: boolean;
  /**Default Sort Direction */
  sortDirection?: 'ascending' | 'descending';
  /**Secondary Sort Direction */
  secondarySort?: 'ascending' | 'descending';
  /**Only show this item inside dropdown sort */
  dropDownOnly?: boolean;
  /**Only using name to get logo for header */
  isLogo?: boolean;
  /**aria label used for header if displayName is not adequate */
  ariaLabel?: string;
}
interface ISortFilterProps {
  /**List of key, name, displayName*/
  sortList: ISortFilterItem[];
  /**List item's ${name} currently being sorted by*/
  selected: string;
  /**Sort Function required when apiSort is false*/
  handleSort?: React.MouseEventHandler<HTMLButtonElement>;
  /**Handles direction of arrow display*/
  ascending?: boolean;
  /**Param name used for sort direction */
  sortDirectionParam?: string;
  /**Flip sort direction */
  flip?: boolean;
  /**Classname added to component wrapper*/
  className?: string;
  /**apiSort ONLY : paramName is the name in url you want to target*/
  paramName?: string;
}
const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};

const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

/**Takes sortList: (key, name, displayName), handleClick function, handles toggle of ascending/descending, and selected */
export const SortFilter = ({
  sortList,
  selected,
  handleSort,
  ascending,
  sortDirectionParam,
  className,
  paramName,
}: ISortFilterProps) => {
  const { currentSite } = useSite();
  const router = useRouter();
  const siteColor =
    (currentSite?.linkColor || currentSite?.primaryColor) ?? '#fe3b1f';
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const selectedObject = sortList.find((item) => item.name === selected);

  const getHref = useCallback(
    (sortType = '') => {
      if (paramName && sortDirectionParam) {
        const sortListObject = sortList.find(
          (sortItem) => sortItem.name === sortType,
        );
        const defaultDirection = sortListObject?.sortDirection;
        const secondaryDirection = sortListObject?.secondarySort;

        return {
          pathname: router.pathname,
          query: {
            ...router.query,
            [paramName]: sortType,
            [sortDirectionParam]:
              sortType === selected &&
              router.query[sortDirectionParam] === defaultDirection
                ? secondaryDirection
                : defaultDirection,
          },
        };
      }

      if (paramName) {
        return {
          pathname: router.pathname,
          query: { ...router.query, [paramName]: sortType },
        };
      }
    },
    [
      paramName,
      router.pathname,
      router.query,
      selected,
      sortDirectionParam,
      sortList,
    ],
  );

  return (
    <div className={clsx(styles.filterWrapper, className)}>
      <PrimaryButton
        aria-describedby={id}
        className={styles.btnSelected}
        endIcon={<Arrow className={styles.dropDownArrow} fill={siteColor} />}
        onClick={handleClick}
        variant="text"
      >
        <span className={styles.btnSelectedText}>
          SORT:{' '}
          <span
            aria-label={
              selectedObject?.ariaLabel ??
              (selectedObject?.displayName || 'Choose Sort By')
            }
            className={styles.selectedType}
          >
            {selectedObject?.displayName || 'None'}
          </span>
          {ascending !== null && selectedObject && (
            <ArrowRight
              className={clsx(styles.arrow, {
                [styles.flip]: selectedObject?.flipDirection
                  ? !ascending
                  : ascending,
              })}
              stroke={siteColor}
              strokeWidth="2.0"
            />
          )}
        </span>
      </PrimaryButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={transformOrigin}
      >
        <div className={styles.listWrapper}>
          {sortList?.map((item) => {
            const isSelected = item.name === selected;

            return (
              <React.Fragment key={item.key}>
                {!paramName && handleSort && (
                  <PrimaryButton
                    className={clsx(styles.button, {
                      [styles.active]: isSelected,
                    })}
                    data-flip={item.flipDirection}
                    data-sorttype={item.name}
                    endIcon={
                      isSelected &&
                      ascending !== null && (
                        <ArrowRight
                          className={clsx(styles.arrow, {
                            [styles.flip]: selectedObject?.flipDirection
                              ? !ascending
                              : ascending,
                          })}
                          stroke={'#1d252c'}
                          strokeWidth="2.0"
                        />
                      )
                    }
                    onClick={handleSort}
                    variant="text"
                  >
                    <span aria-label={item.ariaLabel ?? item.displayName}>
                      {item.displayName}
                    </span>
                  </PrimaryButton>
                )}
                {paramName && (
                  <TextLink
                    className={clsx(styles.button, {
                      [styles.active]: isSelected,
                    })}
                    href={getHref(item.name)}
                    onClick={handleClose}
                    underline="none"
                  >
                    <span aria-label={item.ariaLabel ?? item.displayName}>
                      {item.displayName}
                    </span>
                  </TextLink>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Popover>
    </div>
  );
};
